import { type QueryClient, keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AgentSettings, LeadDetail, LetterTemplateKey, TodayListKey, TouchCreate } from "./models";
import { ApiError, api, assertSignIn, currentSignIn } from "./client";
import type { EstatesQuery } from "@/lib/estates-query";
import { type LeadsQuery, PAGE_SIZE } from "@/lib/leads-query";

export const keys = {
  all: ["workspace"] as const,
  today: (limit?: number) => [...keys.all, "today", limit ?? "all"] as const,
  todayList: (key: TodayListKey, page: number) => [...keys.all, "today-list", key, page] as const,
  leads: (query: LeadsQuery) => [...keys.all, "leads", query] as const,
  lead: (id: string) => [...keys.all, "lead", id] as const,
  estates: (query: EstatesQuery) => [...keys.all, "estates", query] as const,
  unlocks: () => [...keys.all, "unlocks"] as const,
  records: (id: string) => [...keys.all, "records", id] as const,
  mailRun: (page = 1) => [...keys.all, "mail-run", page] as const,
  callSession: () => [...keys.all, "call-session"] as const,
  readiness: () => [...keys.all, "readiness"] as const,
  stats: () => [...keys.all, "stats"] as const,
  counties: () => [...keys.all, "counties"] as const,
  letters: (leadIds: string[]) => [...keys.all, "letters", leadIds] as const,
  templates: () => [...keys.all, "letter-templates"] as const,
  agent: () => [...keys.all, "agent"] as const,
  planChoice: () => [...keys.all, "plan-choice"] as const,
};

/** Rows each Today list shows before "Show all". */
export const TODAY_ROWS = 10;

/** Today's five lists, each with its count and its first rows. */
export const useToday = () =>
  useQuery({ queryKey: keys.today(TODAY_ROWS), queryFn: () => api.today(TODAY_ROWS), placeholderData: keepPreviousData });
/** One Today list in full, a page at a time from the server. */
export const useTodayList = (key: TodayListKey | null, page: number) =>
  useQuery({
    queryKey: keys.todayList(key ?? "drop", page),
    queryFn: () => api.todayList(key ?? "drop", page, PAGE_SIZE),
    placeholderData: keepPreviousData,
    enabled: key !== null,
  });
/** The workspace's today, in its timezone: the day every date field defaults to. */
export const useServerDay = () => {
  const query = useQuery({ queryKey: keys.today(1), queryFn: () => api.today(1), staleTime: 60_000 });
  return query.data?.date;
};
export const useLeads = (query: LeadsQuery, enabled = true) =>
  useQuery({ queryKey: keys.leads(query), queryFn: () => api.leads(query), placeholderData: keepPreviousData, enabled });
export const useEstates = (query: EstatesQuery) =>
  useQuery({ queryKey: keys.estates(query), queryFn: () => api.estates(query), placeholderData: keepPreviousData });
export const useUnlocks = () => useQuery({ queryKey: keys.unlocks(), queryFn: api.unlocks });
export const useLead = (id: string) => useQuery({ queryKey: keys.lead(id), queryFn: () => api.lead(id) });
export const useRecords = (id: string) => useQuery({ queryKey: keys.records(id), queryFn: () => api.records(id) });
export const useMailRun = (page = 1) =>
  useQuery({ queryKey: keys.mailRun(page), queryFn: () => api.mailRun(page), placeholderData: keepPreviousData });
export const useCallSession = () => useQuery({ queryKey: keys.callSession(), queryFn: api.callSession });
export const useReadiness = () => useQuery({ queryKey: keys.readiness(), queryFn: api.readiness });
export const useStats = () => useQuery({ queryKey: keys.stats(), queryFn: api.stats, staleTime: 5 * 60_000 });
export const useMailLetters = (leadIds: string[]) =>
  useQuery({ queryKey: keys.letters(leadIds), queryFn: () => api.mailLetters(leadIds), staleTime: Infinity });
export const useLetterTemplates = () => useQuery({ queryKey: keys.templates(), queryFn: api.letterTemplates });
export const useAgent = () => useQuery({ queryKey: keys.agent(), queryFn: api.agent });
/** The pricing page's plans and counties; public, and the same for everyone. */
export const usePlans = () => useQuery({ queryKey: ["plans"], queryFn: api.plans, staleTime: Infinity });
/** The county and plan this account chose, and whether it asked to start. */
export const usePlanChoice = () => useQuery({ queryKey: keys.planChoice(), queryFn: api.planChoice });
/** The live counties, each with whether the agent works it and its lead counts. */
export const useCounties = () => useQuery({ queryKey: keys.counties(), queryFn: api.counties, staleTime: 60_000 });

/** The live counties and the ones the agent works; until the first answer, none. */
export function useWorkedCounties() {
  const counties = useCounties();
  const all = counties.data ?? [];
  const worked = all.filter((county) => county.selected);
  return { all, worked, several: worked.length > 1, isLoading: counties.isLoading };
}

/**
 * Choosing counties changes every list, so after a save every workspace query is cleared
 * and fetched again; the counties themselves take the server's answer at once. Clearing,
 * not just refetching, keeps the previous counties' rows off the screen while the new ones
 * load: a slow Today once showed Cook's letters under the Harris header for minutes.
 * The agent's own settings do not depend on the counties and stay: clearing them once hid
 * the first-run screen in the middle of its save.
 */
/** What a write remembers from when it was sent: the sign-in it belongs to. */
interface SentUnder {
  signIn: number;
}

const sentNow = (): SentUnder => ({ signIn: currentSignIn() });
/** Whether the sign-in a write was sent under is still the one in force. */
const stillSignedIn = (sent: SentUnder | undefined) => sent?.signIn === currentSignIn();

export function useSaveCounties() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.saveCounties,
    onMutate: sentNow,
    onSuccess: (saved, _counties, sent) => {
      if (!stillSignedIn(sent)) return;
      client.setQueryData(keys.counties(), saved);
      void client.resetQueries({ queryKey: keys.all, predicate: (query) => !["counties", "agent"].includes(String(query.queryKey[1])) });
    },
    onError: (error) => {
      toast.error(errorMessage(error));
    },
  });
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : "Something went wrong.";
}

/**
 * Every write changes which list a lead sits on, so after one the lead's page takes the
 * answer and every other query is refetched; an answer that arrives after a sign-out or
 * another sign-in is dropped.
 */
export function leadWriteOptions<V>(client: QueryClient, write: (id: string, vars: V) => Promise<LeadDetail>) {
  return {
    mutationFn: ({ id, vars }: { id: string; vars: V }) => write(id, vars),
    onMutate: sentNow,
    onSuccess: (lead: LeadDetail, _vars: { id: string; vars: V }, sent: SentUnder | undefined) => {
      if (!stillSignedIn(sent)) return;
      client.setQueryData(keys.lead(lead.lead_id), lead);
      void client.invalidateQueries({
        queryKey: keys.all,
        predicate: (q) => !(q.queryKey[1] === "lead" && q.queryKey[2] === lead.lead_id),
      });
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error));
    },
  };
}

function useLeadWrite<V>(write: (id: string, vars: V) => Promise<LeadDetail>) {
  return useMutation(leadWriteOptions(useQueryClient(), write));
}

export const useLogTouch = () => useLeadWrite<TouchCreate>((id, body) => api.logTouch(id, body));
export const useDrop = () => useLeadWrite<undefined>((id) => api.drop(id));
export const useUndrop = () => useLeadWrite<undefined>((id) => api.undrop(id));
export const useAddNote = () => useLeadWrite<string>((id, text) => api.addNote(id, text));
export const useAcknowledgeApproval = () => useLeadWrite<undefined>((id) => api.acknowledgeApproval(id));

/** Unlocking moves an estate onto the agent's lists, so every list is read again. */
export function useUnlockEstate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.unlockEstate,
    onMutate: sentNow,
    onSuccess: (_result, _estate, sent) => {
      if (stillSignedIn(sent)) void client.invalidateQueries({ queryKey: keys.all });
    },
    onError: (error, _estate, sent) => {
      // The estate may have closed or the month run out while the list was on screen.
      if (stillSignedIn(sent)) void client.invalidateQueries({ queryKey: keys.all });
      toast.error(errorMessage(error));
    },
  });
}

export function useMarkSent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.markSent,
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => {
      toast.error(errorMessage(error));
    },
  });
}

type TemplateChange = { key: LetterTemplateKey; purpose: string; body: string };

/** Saves the letters one by one, and stops if the sign-in they started under ends meanwhile. */
export async function saveTemplatesInOrder(changes: TemplateChange[], save = api.saveLetterTemplate) {
  const signIn = currentSignIn();
  for (const change of changes) {
    assertSignIn(signIn);
    await save(change.key, { purpose: change.purpose, body: change.body });
  }
}

/** Saves the changed letters one by one; the print page then merges them again. */
export function useSaveLetterTemplates() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (changes: TemplateChange[]) => saveTemplatesInOrder(changes),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.templates() });
      void client.invalidateQueries({ queryKey: [...keys.all, "letters"] });
    },
    onError: (error) => {
      toast.error(errorMessage(error));
    },
  });
}

/**
 * Saving the agent's settings. A read of them still out when the save answers was sent
 * before it and would put the old settings back (the first-run screen came back after its
 * Open Today), so the save cancels such reads, whenever they started, before taking its answer.
 */
export function saveAgentOptions(client: QueryClient, save: typeof api.saveAgent = api.saveAgent) {
  return {
    mutationFn: save,
    onMutate: async (): Promise<SentUnder> => {
      const sent = sentNow();
      await client.cancelQueries({ queryKey: keys.agent() });
      return sent;
    },
    onSuccess: async (saved: AgentSettings, _body: unknown, sent: SentUnder | undefined) => {
      if (!stillSignedIn(sent)) return;
      await client.cancelQueries({ queryKey: keys.agent() });
      client.setQueryData(keys.agent(), saved);
      void client.invalidateQueries({ queryKey: [...keys.all, "letters"] });
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error));
    },
  };
}

/** The account's subscription as Stripe last gave it, or null without one. */
export const useBilling = () => useQuery({ queryKey: ["billing"], queryFn: api.billing });
export const TWO_STEP_KEY = ["two-step"] as const;
export const useTwoStep = () => useQuery({ queryKey: TWO_STEP_KEY, queryFn: api.twoStep });

/** What these counties bring, before paying; the figures change slowly, so they are kept a few minutes. */
export function useProof(counties: string[]) {
  return useQuery({
    queryKey: ["proof", ...counties],
    queryFn: () => api.proof(counties),
    enabled: counties.length > 0,
    staleTime: 5 * 60_000,
  });
}

/** Asks to start a plan; until billing exists the admin answers by granting its counties. */
export function useRequestPlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.requestPlan,
    onMutate: sentNow,
    onSuccess: (choice, _body, sent) => {
      if (stillSignedIn(sent)) client.setQueryData(keys.planChoice(), choice);
    },
  });
}

export function useSaveAgent() {
  const client = useQueryClient();
  return useMutation(saveAgentOptions(client));
}

export function useSetTimezone() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.setTimezone,
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => {
      toast.error(errorMessage(error));
    },
  });
}
